/**
 * "Разбивка оценки" card — A-F component scores + 5 quality sub-scores + confidence.
 *
 * The overall hero score and the attention flag now live in <HeroScore>
 * (rendered at the top of AnalysisPanel), so this section is purely the
 * collapsible breakdown.
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { isComponentMetric, type QualitySubMetricKey } from '@aica/core';
import {
  AnalysisSection,
  scoreTextColor,
  scoreBgColor,
  SCORE_MAP,
  BREAKDOWN_KEYS,
  type XMLAnalysisResult,
  type ComponentScores,
} from './analysis-utils';

interface QualityScoreSectionProps {
  result: XMLAnalysisResult;
  componentScores?: ComponentScores;
  confidencePercent?: number;
  /**
   * The share of each criterion in this call's score, when the project's
   * weights weight it (#874): shown next to the criterion's row.
   */
  weightShares?: Partial<Record<QualitySubMetricKey, number>>;
}

/** A share as a whole percent ("weight 38 %"): the rows read at a glance, not to a decimal. */
const formatShare = (share: number): string => String(Math.round(share));

export function QualityScoreSection({
  result,
  componentScores,
  confidencePercent,
  weightShares,
}: QualityScoreSectionProps) {
  const { t } = useTranslation('records');
  const breakdown = result.summary?.qualityScoreBreakdown;

  // At least one A-F component score present? Render ALL 6 rows (missing
  // ones as muted "—") so the letters stay sequential — a partially-scored
  // call used to filter down to e.g. "A, C, E, F", which read as a bug.
  // A record with no component scores at all still shows nothing (below).
  //
  // typeof === 'number' (not `!== undefined`): a score can be null, not just
  // absent, when its metric was never applicable to the call (e.g.
  // closingScore) — null must count the same as "no data", never as "a
  // criterion is filled".
  const hasAnyAgScore = SCORE_MAP.some(
    ({ key }) => typeof componentScores?.[key] === 'number' && isComponentMetric(key)
  );
  const agRows = hasAnyAgScore ? SCORE_MAP : [];
  const subRows = BREAKDOWN_KEYS.filter(({ field }) => breakdown?.[field] !== undefined);

  // No breakdown data at all → no card (the hero already shows the overall score).
  if (agRows.length === 0 && subRows.length === 0) return null;

  // Badge counts FILLED criteria only (owner decision 2026-07-17): agRows
  // renders all 6 letters incl. muted "—" placeholders, which must not
  // inflate the count. Same null-vs-absent guard as hasAnyAgScore above.
  const filledAgCount = agRows.filter(
    ({ key }) => typeof componentScores?.[key] === 'number'
  ).length;
  const criteriaCount = filledAgCount + subRows.length;

  return (
    <AnalysisSection
      title={t('records.analysisPanel.scoreBreakdown')}
      summary={
        <span className="font-mono text-2xs text-fg-subtle">
          {t('records.analysisPanel.criteriaCount', { count: criteriaCount })}
        </span>
      }
      defaultOpen
    >
      {/* A-F component scores */}
      {agRows.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {agRows.map(({ key, code, labelKey }) => {
            const val = componentScores?.[key];
            // typeof === 'number' (not `!== undefined`): a component score
            // can be null, not just absent, when its metric was never
            // applicable to this call (e.g. closingScore) — `!== undefined`
            // would let null through into scoreTextColor/scoreBgColor and
            // render a blank, wrongly-colored bar instead of the muted "—"
            // below. No live producer sends null here today (both
            // extractAnalysisMetrics and the prompt-builder preview omit the
            // key instead), but this guard is what makes that safe to rely
            // on rather than accidental.
            const hasVal = typeof val === 'number';
            const share = weightShares?.[key as QualitySubMetricKey];
            return (
              <div key={key} className="flex items-center gap-3">
                <span className="w-3 shrink-0 font-mono text-xs text-fg-subtle">
                  {code}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-fg-secondary">
                  {t(labelKey)}
                </span>
                {share !== undefined && (
                  <span
                    data-testid={`quality-weight-share-${key}`}
                    className="shrink-0 font-mono text-2xs text-fg-subtle"
                  >
                    {t('records.analysisPanel.qualityScore.weightShare', {
                      value: formatShare(share),
                    })}
                  </span>
                )}
                <span
                  className={cn(
                    'w-7 shrink-0 text-right font-mono text-xs font-semibold',
                    hasVal ? scoreTextColor(val) : 'text-fg-faint'
                  )}
                >
                  {hasVal ? val : '—'}
                </span>
                <span className="h-[5px] w-[78px] shrink-0 overflow-hidden rounded-full bg-bg-subtle">
                  {hasVal && (
                    <span
                      className={cn('block h-full rounded-full', scoreBgColor(val))}
                      style={{ width: `${val}%` }}
                    />
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* 5 quality sub-scores */}
      {subRows.length > 0 && (
        <div className={cn(agRows.length > 0 && 'mt-3 border-t border-border pt-3')}>
          <div className="mb-2 font-mono text-micro font-semibold uppercase tracking-wide text-fg-muted">
            {t('records.analysisPanel.qualityScoreBreakdown')}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {subRows.map(({ field, labelKey }) => {
              const val = breakdown![field]!;
              return (
                <div
                  key={field}
                  className="flex items-center justify-between rounded bg-bg-surface px-2 py-1.5 text-xs"
                >
                  <span className="text-fg-muted">{t(labelKey)}</span>
                  <span className={cn('font-mono font-semibold', scoreTextColor(val))}>
                    {val}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confidence footer */}
      {confidencePercent !== undefined && (
        <div className="mt-3 flex items-center gap-2 border-t border-border pt-3 text-xs">
          <span className="text-fg-muted">{t('records.analysisPanel.confidence')}</span>
          <span className="font-mono font-semibold text-primary">
            {confidencePercent}%
          </span>
        </div>
      )}
    </AnalysisSection>
  );
}
